/**
 * Standard-Base64-Helpers (mit Padding) ohne `atob`/`btoa`.
 *
 * Konventionen verbieten `atob`/`btoa`, daher manuelles Alphabet. Standard-Base64
 * (nicht URL-safe!) wird hier für SPKI-Encoding genutzt — SPKI-Public-Keys liegen
 * im Konfigurations-Wireformat in normalem Base64 mit `+/=` vor.
 *
 * URL-safe Base64 (`-_`, ohne Padding) ist Sache von DEV-005 (TokenCodec).
 *
 * @see tickets/EPIC-001-patient-terminabsage/AFO-002-shared-vertrag/afo.md
 */

const BASE64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
const BASE64_PAD = '=';
const BITS_PER_BYTE = 8;
const BITS_PER_CHAR = 6;
const BYTE_MASK = 0xff;
const SEXTET_MASK = 0x3f;
const GROUP_INPUT_BYTES = 3;
const GROUP_OUTPUT_CHARS = 4;

const CHAR_TO_VALUE: ReadonlyMap<string, number> = buildCharToValue();

function buildCharToValue(): Map<string, number> {
  const map = new Map<string, number>();
  for (let index = 0; index < BASE64_ALPHABET.length; index++) {
    const char = BASE64_ALPHABET[index];
    if (char === undefined) {
      continue;
    }
    map.set(char, index);
  }
  return map;
}

/**
 * Kodiert ein Byte-Array als Standard-Base64-String mit Padding.
 *
 * @param bytes Roh-Bytes (z. B. SPKI-DER).
 * @returns Base64-String mit `=`-Padding bei Bedarf.
 */
export function bytesToBase64(bytes: Uint8Array): string {
  const fullGroups = Math.floor(bytes.length / GROUP_INPUT_BYTES);
  const remainder = bytes.length % GROUP_INPUT_BYTES;
  const output: string[] = [];

  for (let groupIndex = 0; groupIndex < fullGroups; groupIndex++) {
    const offset = groupIndex * GROUP_INPUT_BYTES;
    output.push(encodeTriplet(bytes, offset));
  }

  if (remainder > 0) {
    output.push(encodeRemainder(bytes, fullGroups * GROUP_INPUT_BYTES, remainder));
  }

  return output.join('');
}

function encodeTriplet(bytes: Uint8Array, offset: number): string {
  const byte0 = bytes[offset] ?? 0;
  const byte1 = bytes[offset + 1] ?? 0;
  const byte2 = bytes[offset + 2] ?? 0;
  const combined = (byte0 << (BITS_PER_BYTE * 2)) | (byte1 << BITS_PER_BYTE) | byte2;
  return (
    charAt((combined >> (BITS_PER_CHAR * 3)) & SEXTET_MASK) +
    charAt((combined >> (BITS_PER_CHAR * 2)) & SEXTET_MASK) +
    charAt((combined >> BITS_PER_CHAR) & SEXTET_MASK) +
    charAt(combined & SEXTET_MASK)
  );
}

function encodeRemainder(bytes: Uint8Array, offset: number, remainder: number): string {
  const byte0 = bytes[offset] ?? 0;
  const byte1 = remainder === 2 ? (bytes[offset + 1] ?? 0) : 0;
  const combined = (byte0 << (BITS_PER_BYTE * 2)) | (byte1 << BITS_PER_BYTE);

  if (remainder === 1) {
    return (
      charAt((combined >> (BITS_PER_CHAR * 3)) & SEXTET_MASK) +
      charAt((combined >> (BITS_PER_CHAR * 2)) & SEXTET_MASK) +
      BASE64_PAD +
      BASE64_PAD
    );
  }
  return (
    charAt((combined >> (BITS_PER_CHAR * 3)) & SEXTET_MASK) +
    charAt((combined >> (BITS_PER_CHAR * 2)) & SEXTET_MASK) +
    charAt((combined >> BITS_PER_CHAR) & SEXTET_MASK) +
    BASE64_PAD
  );
}

function charAt(index: number): string {
  const char = BASE64_ALPHABET[index];
  if (char === undefined) {
    throw new RangeError(`Base64-Alphabet-Index out of range: ${index.toString()}`);
  }
  return char;
}

/**
 * Dekodiert einen Standard-Base64-String (mit oder ohne Padding) in Bytes.
 *
 * Wirft `RangeError`, wenn der Input ungültige Zeichen enthält oder die Länge
 * nicht zu einer validen Base64-Sequenz passt.
 *
 * @param input Standard-Base64-String.
 * @returns Dekodierte Bytes.
 */
export function base64ToBytes(input: string): Uint8Array {
  const stripped = input.replace(/=+$/u, '');
  if (stripped.length === 0) {
    return new Uint8Array(0);
  }

  const outputLength = computeDecodedLength(stripped.length);
  const output = new Uint8Array(outputLength);
  let writeIndex = 0;

  for (let readIndex = 0; readIndex < stripped.length; readIndex += GROUP_OUTPUT_CHARS) {
    const chunkLength = Math.min(GROUP_OUTPUT_CHARS, stripped.length - readIndex);
    writeIndex += decodeChunk(stripped, readIndex, chunkLength, output, writeIndex);
  }

  return output;
}

function computeDecodedLength(strippedLength: number): number {
  const remainder = strippedLength % GROUP_OUTPUT_CHARS;
  if (remainder === 1) {
    throw new RangeError('Invalid Base64 length: 4n+1 is impossible.');
  }
  const fullGroups = Math.floor(strippedLength / GROUP_OUTPUT_CHARS);
  const tailBytes = remainder === 0 ? 0 : remainder - 1;
  return fullGroups * GROUP_INPUT_BYTES + tailBytes;
}

function decodeChunk(
  input: string,
  readIndex: number,
  chunkLength: number,
  output: Uint8Array,
  writeIndex: number,
): number {
  const sextet0 = lookup(input[readIndex]);
  const sextet1 = lookup(input[readIndex + 1]);
  const sextet2 = chunkLength > 2 ? lookup(input[readIndex + 2]) : 0;
  const sextet3 = chunkLength > 3 ? lookup(input[readIndex + 3]) : 0;

  const combined =
    (sextet0 << (BITS_PER_CHAR * 3)) |
    (sextet1 << (BITS_PER_CHAR * 2)) |
    (sextet2 << BITS_PER_CHAR) |
    sextet3;

  output[writeIndex] = (combined >> (BITS_PER_BYTE * 2)) & BYTE_MASK;
  let written = 1;
  if (chunkLength > 2) {
    output[writeIndex + 1] = (combined >> BITS_PER_BYTE) & BYTE_MASK;
    written = 2;
  }
  if (chunkLength > 3) {
    output[writeIndex + 2] = combined & BYTE_MASK;
    written = 3;
  }
  return written;
}

function lookup(char: string | undefined): number {
  if (char === undefined) {
    throw new RangeError('Unexpected end of Base64 input.');
  }
  const value = CHAR_TO_VALUE.get(char);
  if (value === undefined) {
    throw new RangeError(`Invalid Base64 character: ${JSON.stringify(char)}`);
  }
  return value;
}
